import { getTreatmentIsEnabled } from '@wf-mfe/toggles'
import { redirectToLogin, shouldRedirectToLogin } from './redirectToLogin.js'
import {
  isRedRockUrl,
  isSpringControllerUrl,
  isStreamApiUrl,
} from './requestTypes.js'
import {
  getCacheKeyForRequest,
  getResultsFromCache,
  cacheResponseData,
} from './caching.js'
import { unwrapResponse } from './utilities.js'
import { getWorkfrontRootApiUrl } from './configuration'
import getFetchOptions from './getFetchOptions.js'
import { getInitialTrackingObject, trackRequest } from './trackingFetch.js'

/**
 * Wrapped fetch call that can communicate with the Stream API,
 * Spring Controller and other endpoints.
 * Redirects to login if user is not authenticated.
 * @param {string} url   endpoint url to make the request to
 * @param {RequestInit} [fetchOptions]   fetch-compatible options object
 * @param {object} [workfrontOptions]   Workfront-specific options object
 * @returns {Promise<String|Object> | never}   Unwraps response based on request type (json or text)
 */
export async function wfetch(url, fetchOptions = {}, workfrontOptions = {}) {
  const {
    timeToExpiration,
    handleOkResponse,
    handleErrorResponse,
    preventAutoRedirect,
  } = workfrontOptions

  const cacheKey = getCacheKeyForRequest(url, fetchOptions, workfrontOptions)
  const resultsFromCache = getResultsFromCache(cacheKey)

  if (resultsFromCache) {
    return resultsFromCache
  }

  const fullUrl = getWorkfrontRootApiUrl() + url
  try {
    const tracking = getInitialTrackingObject(fetchOptions)

    const mergedFetchOptions = getFetchOptions(url, fetchOptions, tracking)

    const response = await fetch(fullUrl, mergedFetchOptions)

    trackRequest(url, tracking)

    if (shouldRedirectToLogin(response) && !preventAutoRedirect) {
      return await redirectToLogin()
    }

    if (response.ok) {
      if (handleOkResponse) {
        return handleOkResponse(response)
      }

      if (isRedRockUrl(url)) {
        const responseData = await unwrapRedRockResponse(url, response)

        cacheResponseData(cacheKey, responseData, timeToExpiration)

        return responseData
      } else {
        return response
      }
    } else {
      if (handleErrorResponse) {
        return handleErrorResponse(response)
      }

      let message = response.statusText

      try {
        const errorData = await response.json()
        message = errorData?.error?.message
      } catch (error) {
        console.error(error)
      }

      const responseError = new Error(
        `\n\t${response.status}: ${message}\n\t${url}`
      )

      responseError.response = response

      throw responseError
    }
  } catch (error) {
    throw error instanceof Error ? error : new Error(error)
  }
}

async function unwrapRedRockResponse(url, response) {
  const data = await unwrapResponse(response)
  let responseData

  if (isStreamApiUrl(url)) {
    if (data.error) {
      const throwObjectError = await getTreatmentIsEnabled(
        'spring-controller-throw-real-error'
      )
      if (throwObjectError) {
        throw errorWithTitle(data.error.message, data.error.title)
      } else {
        throw new Error(
          `\n\t${response.status}: ${data.error.message}\n\t${url}`
        )
      }
    }

    responseData = data?.data?.result ?? data?.data

    if (url.includes('/attask/api-internal/batch')) {
      responseData = responseData.map(
        (batchData) => batchData?.data?.result ?? batchData?.data ?? batchData
      )
    }
  } else if (isSpringControllerUrl(url)) {
    const shouldThrowRealError = await getTreatmentIsEnabled(
      'spring-controller-throw-real-error'
    )
    if (shouldThrowRealError && data.error) {
      throw errorWithTitle(data.error.message, data.error.title)
    }

    responseData = data?.data?.data ?? data?.data ?? data
  }

  return responseData
}

function errorWithTitle(message, title) {
  const error = new Error(message)
  error.title = title
  return error
}
